/**
 * Usage example
 *
 * const hasAccess = useProjectAccess()
 *
 * hasAccess(PERMISSIONS.PROJECT_SETTINGS_ACCESS)
 *
 * https://docs.google.com/spreadsheets/d/1zedWqE5rpVjdKPcjdYCmAmuR86uGZ9wPwWP7mu6GABk/edit#gid=0
 */

import { shallowEqual, useSelector } from 'react-redux'

import { PERMISSION_TYPE } from '../permissions'
import type { PROJECT_PERMISSION } from '../permissions/project/permissions'
import type { AppState } from '../types'

type AccessType = {
  hasAccess: boolean
  hasLimitedAccess: boolean
  hasNotAvailableAccess: boolean
  hasNotAvailableAccessBadge: boolean
  badgeType: string | null
}

const useProjectAccess = (permission: PROJECT_PERMISSION): AccessType =>
  useSelector<AppState, AccessType>(({ user }) => {
    if (
      user.permissions === null ||
      user.permissions[permission] === undefined
    ) {
      return {
        hasAccess: false,
        hasLimitedAccess: false,
        hasNotAvailableAccess: false,
        hasNotAvailableAccessBadge: false,
        badgeType: null,
      }
    }

    const permissionValue = user.permissions[permission]
    const badgeType = permissionValue.badge ? permissionValue.badge : null
    return {
      hasAccess: permissionValue.type === PERMISSION_TYPE.YES,
      hasLimitedAccess: permissionValue.type === PERMISSION_TYPE.LIMITED,
      hasNotAvailableAccess: permissionValue.type === PERMISSION_TYPE.NA,
      hasNotAvailableAccessBadge:
        permissionValue.type === PERMISSION_TYPE.NA_BADGE,
      badgeType,
    }
  }, shallowEqual)

export default useProjectAccess
